import { useMemo } from "react";
import { applyLocalFilters } from "./tableLogic";
import type { Filter } from "./tableLogic";
import { applyCalcCols } from "./ListUtils";

type PipelineParams<T> = {
  data: T[];
  search: string;
  searchableFields?: string[];
  filters: Filter[]; // <-- Corrigido aqui
  sortCol: string | null;
  sortDir: "asc" | "desc";
  page: number;
  pageSize: number;
  customColumns: { name: string; expr: string }[];
};

export function useDataPipeline<T extends Record<string, unknown>>({
  data,
  search,
  searchableFields,
  filters,
  sortCol,
  sortDir,
  page,
  pageSize,
  customColumns,
}: PipelineParams<T>) {
  // Search
  const searched = useMemo(() => {
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter(row => {
      const fields = searchableFields?.length ? searchableFields : Object.keys(row as object);
      return fields.some(f => String((row as Record<string, unknown>)[f] ?? "").toLowerCase().includes(s));
    });
  }, [data, search, searchableFields]);

  // Filter
  const filtered = useMemo(
    () => applyLocalFilters(searched, filters),
    [searched, filters]
  );

  // Sort
  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortCol];
      const bVal = b[sortCol];
      if (aVal === bVal) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "number" && typeof bVal === "number")
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      return sortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filtered, sortCol, sortDir]);

  // Paginate
  const paginated = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize]
  );

  // Calculated columns
  const withCalc = useMemo(
    () => applyCalcCols(paginated, customColumns),
    [paginated, customColumns]
  );

  return {
    data: withCalc,
    total: sorted.length,
    filtered,
    sorted,
    paginated,
  };
}